import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { scanService } from '../../services/api';
import { useThemeColors } from '../../hooks/useThemeColors';
import HttpTransactionView, { parseHttpTransactionFromRaw } from '../common/HttpTransactionView';
import '../ScanResults.css';

/**
 * ScrutinizeResults - SCRUTINIZE Deep Analysis Results Page (v2.0 Fixed)
 * Updated with correct data path mappings to match actual JSON structure
 * Primary Colors: Brand Red (headers), Neon Blue (data), Neon Green (positive/actions)
 */

const ScrutinizeResults = () => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    targetProfile: true,
    attackSurface: true,
    attackSurfaceDetails: false,
    r3dteamBrief: true,
    vulnerabilities: false,
    cloudInfrastructure: false,
    attackIntelligence: true,
    mitreDetails: false,
    pentestRoadmap: false,
    defensivePosture: true,
    recommendations: true,
    compliance: false,
    threatIntel: false,
    toolSummaries: false,
    rawToolData: false,
    fullJson: false
  });
  const [showAllVulns, setShowAllVulns] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [expandedVulns, setExpandedVulns] = useState({});

  const { scanId } = useParams();
  const navigate = useNavigate();
  const themeColors = useThemeColors();

  // Color scheme constants - theme-aware via useThemeColors hook
  const COLORS = {
    brandRed: themeColors.brand,
    neonBlue: themeColors.accent.primary,
    neonGreen: themeColors.risk.healthy,
    textPrimary: themeColors.text.primary,
    textSecondary: themeColors.text.secondary,
    textMuted: themeColors.text.muted,
    bgPrimary: themeColors.bg.primary,
    bgSecondary: themeColors.bg.secondary,
    bgCard: themeColors.bg.card,
    bgHover: themeColors.bg.card,
    borderDark: themeColors.border.dark,
    borderMedium: themeColors.border.medium,
    severityCritical: themeColors.severity.critical,
    severityHigh: themeColors.severity.high,
    severityMedium: themeColors.severity.medium,
    severityLow: themeColors.severity.low,
    severityInfo: themeColors.severity.info
  };
  
  useEffect(() => {
    if (!scanId) {
      setError('No scan ID provided');
      setLoading(false);
      return;
    }
    fetchResults(scanId);
  }, [scanId]);
  
  const fetchResults = async (scanId) => {
    try {
      setLoading(true);
      const data = await scanService.getScrutinizeResult(scanId);
      if (!data) {
        setError('Scan results not found.');
        setLoading(false);
        return;
      }
      setResults(data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  const handleDownloadPDF = async () => {
    if (pdfGenerating || !results) return;
    setPdfGenerating(true);
    try {
      const { pdf } = await import('@react-pdf/renderer');
      const { default: saveAs } = await import('file-saver');
      const { default: ScanReportPDFComp } = await import('../reports/ScanReportPDF');
      const scanData = results.scanData || {};
      const element = React.createElement(ScanReportPDFComp, {
        scanResult: {
          ...results,
          target: scanData.target || results.target,
          riskScore: scanData.riskScore || results.riskScore,
          riskLevel: scanData.riskLevel || results.riskLevel,
          nuclei: { vulnerabilities: scanData.vulnerabilities?.findings || [] },
          naabu: scanData.network?.ports || {},
          httpx: scanData.web?.httpProbes || {},
          subfinder: scanData.dns?.subdomains?.subfinder || {},
          geolocation: scanData.geolocation || {},
          aiAnalysis: scanData.aiAnalysis || results.aiAnalysis || {},
          webTechnology: scanData.web?.httpProbes || {},
        },
        domain: scanData.target || results.target,
        scanDate: results.timestamp
      });
      const blob = await pdf(element).toBlob();
      saveAs(blob, `driftalarm-scrutinize-${scanData.target || 'report'}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };
  
  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };
  
  const getSeverityColor = (severity) => {
    if (!severity) return COLORS.textMuted;
    const colors = { 
      'Critical': COLORS.severityCritical, 
      'critical': COLORS.severityCritical,
      'High': COLORS.severityHigh, 
      'high': COLORS.severityHigh,
      'Medium': COLORS.severityMedium, 
      'medium': COLORS.severityMedium,
      'Low': COLORS.severityLow, 
      'low': COLORS.severityLow,
      'Info': COLORS.severityInfo,
      'info': COLORS.severityInfo
    };
    return colors[severity] || COLORS.textMuted;
  };
  
  const getSeverityOrder = (severity) => {
    const order = { 'critical': 0, 'high': 1, 'medium': 2, 'low': 3, 'info': 4 };
    return order[severity?.toLowerCase()] ?? 5;
  };
  
  const getPhaseColor = (phase) => {
    const colors = { 1: COLORS.neonBlue, 2: COLORS.brandRed, 3: COLORS.neonGreen, 4: COLORS.neonBlue };
    return colors[phase] || COLORS.textMuted;
  };
  
  if (loading) {
    return (
      <div className="scan-results-container">
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: '60px', height: '60px', border: '5px solid #333', borderTop: `5px solid ${COLORS.brandRed}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <p style={{ color: COLORS.brandRed, fontSize: '1.3rem', marginTop: '25px', textTransform: 'uppercase', letterSpacing: '4px' }}>Deep Analysis in Progress...</p>
          <p style={{ color: COLORS.textMuted, fontSize: '0.9rem' }}>Deep Scan results loading</p>
        </div>
      </div>
    );
  }
  
  if (error || !results) {
    return (
      <div className="scan-results-container">
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h2 style={{ color: COLORS.severityCritical, marginBottom: '15px' }}>{error ? 'ERROR' : 'NO RESULTS'}</h2>
          <p style={{ color: COLORS.textMuted, marginBottom: '25px' }}>{error || 'No scan results found'}</p>
          <button onClick={() => navigate('/scrutinize')} style={{ padding: '12px 30px', background: COLORS.brandRed, border: 'none', borderRadius: '6px', color: '#fff', fontWeight: '600', cursor: 'pointer' }}>
            Return to Deep Scan
          </button>
        </div>
      </div>
    );
  }
  
  // ============================================================
  // CORRECT DATA PATH MAPPINGS FOR SCRUTINIZE JSON STRUCTURE
  // ============================================================
  
  const scanData = results.scanData || {};
  
  // DNS data
  const dns = scanData.dns || results.dns || {};
  const dnsResolution = dns.resolution || {};

  const dnsSubdomains = dns.subdomains || {};
  
  // Subfinder data (nested under dns.subdomains)
  const subfinder = dnsSubdomains.subfinder || {};
  const subfinderSummary = subfinder.summary || {};

  
  // Network data (ports = naabu equivalent, services = zgrab2 equivalent)
  const network = scanData.network || {};
  const ports = network.ports || {};
  const portServices = ports.services || [];
  const portSummary = ports.summary || {};
  const riskClassification = ports.riskClassification || {};
  const remoteAccessExposed = ports.remoteAccessExposed || [];
  const databasesExposed = ports.databasesExposed || [];
  
  const networkServices = network.services || {};

  const servicesList = networkServices.services || [];
  const sshInfo = networkServices.sshInfo || {};

  const serviceSummary = networkServices.summary || {};
  
  // Web data
  const web = scanData.web || {};
  
  // httpProbes = httpx equivalent
  const httpProbes = web.httpProbes || {};
  const probes = httpProbes.probes || [];
  const httpTechnologies = httpProbes.technologies || [];
  const httpSummary = httpProbes.summary || {};
  
  // crawling = katana equivalent
  const crawling = web.crawling || {};

  const crawlingParams = crawling.parameters || {};
  const crawlingSummary = crawling.summary || {};
  
  // waf = wafw00f equivalent
  const waf = web.waf || {};
  
  // technologies = whatweb equivalent
  const webTech = web.technologies || {};
  
  // SSL data
  const ssl = scanData.ssl || {};


  
  // Vulnerabilities = nuclei equivalent
  const vulnerabilities = scanData.vulnerabilities || {};
  const nucleiVulnList = vulnerabilities.vulnerabilities || [];
  const vulnSummary = vulnerabilities.summary || {};
  const cveList = vulnerabilities.cveList || [];

  // Nikto web server findings (FIX: Add nikto to vulnerability display)
  const serverScan = web.serverScan || {};

  // Extract nikto vulnerabilities (higher severity)
  const niktoVulns = (serverScan.vulnerabilities || []).map(v => ({
    ...v,
    source: 'nikto',
    templateId: v.id || 'NIKTO',
    name: v.description ? v.description.substring(0, 80) : 'Web Server Finding',
    severity: v.severity || 'Medium',
  }));

  // Extract nikto informational findings
  const niktoInfo = (serverScan.informational || []).map(v => ({
    ...v,
    source: 'nikto',
    templateId: v.id || 'NIKTO-INFO',
    name: v.description ? v.description.substring(0, 80) : 'Web Server Info',
    severity: v.severity || 'Info',
  }));

  // Combine ALL sources: nuclei + nikto vulns + nikto info
  const vulnList = [...nucleiVulnList, ...niktoVulns, ...niktoInfo];

  // Recalculate severity counts from merged vulnerability list (not nuclei-only counts)
  const mergedSeverityCounts = vulnList.reduce((acc, v) => {
    const sev = (v.severity || 'info').toLowerCase();
    if (sev in acc) acc[sev] += 1;
    return acc;
  }, { critical: 0, high: 0, medium: 0, low: 0, info: 0 });

  // Geolocation and WHOIS
  const geo = scanData.geolocation || results.geolocation || {};
  const whois = scanData.whois || results.whois || {};
  
  // Executive Summary
  const execSummary = scanData.executiveSummary || results.executiveSummary || {};
  const stats = execSummary.statistics || {};
  
  // Metadata
  const metadata = scanData.metadata || results.metadata || {};
  
  // AI Analysis (at top level, not inside scanData)
  const ai = results.aiAnalysis || {};
  const aiAttackSurface = ai.attackSurface || {};
  const mitreMapping = ai.mitreAttackMapping || [];
  const pentestRoadmap = ai.penetrationTestingRoadmap || {};
  const complianceMapping = ai.complianceMapping || {};
  const threatIntel = ai.threatIntelligence || {};
  const prioritizedRecs = ai.prioritizedRecommendations || [];

  // Cloud Infrastructure data (from nuclei cloud templates)
  const cloudData = scanData.cloudInfrastructure || results.cloudInfrastructure || {};
  const cloudFindings = vulnerabilities.byType?.cloud || [];
  const azureTenantId = cloudData.azure?.tenantId || cloudFindings.find(f => f.templateId?.includes('azure-domain-tenant'))?.extractedResults?.[0];
  const cloudProviders = {
    aws: cloudData.aws || { detected: false, buckets: [] },
    azure: cloudData.azure || { detected: !!azureTenantId, tenantId: azureTenantId, containers: [] },
    gcp: cloudData.gcp || { detected: false, buckets: [] }
  };
  const hasCloudFindings = cloudFindings.length > 0 || cloudProviders.aws.detected || cloudProviders.azure.detected || cloudProviders.gcp.detected;

  // Sort vulnerabilities by severity (highest first)
  const sortedVulns = [...vulnList].sort((a, b) => getSeverityOrder(a.severity) - getSeverityOrder(b.severity));
  const displayVulns = showAllVulns ? sortedVulns.slice(0, 15) : sortedVulns.slice(0, 5);
  
  // Combine technologies from multiple sources
  const allTechnologies = [
    ...httpTechnologies,
    ...(webTech.technologies || [])
  ].filter((v, i, a) => a.indexOf(v) === i);
  
  // Get parameters as array
  const parameterNames = Object.keys(crawlingParams);
  
  return (
    <div className="scan-results-container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 25px' }}>
      
      {/* ==================== HEADER ==================== */}
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ 
          fontSize: '3rem', 
          fontWeight: '900', 
          color: COLORS.brandRed,
          textTransform: 'uppercase',
          letterSpacing: '8px',
          marginBottom: '8px',
          textShadow: `0 0 40px ${COLORS.brandRed}80`
        }}>DEEP SCAN</h1>
        <p style={{ color: COLORS.textMuted, fontSize: '1rem', letterSpacing: '3px', textTransform: 'uppercase' }}>
          Deep Analysis • Comprehensive Security Assessment
        </p>
        <button
          className="sr-btn-secondary sr-pdf-btn"
          onClick={handleDownloadPDF}
          disabled={pdfGenerating}
          style={{ marginTop: '12px' }}
        >
          {pdfGenerating ? 'Generating...' : 'Download PDF'}
        </button>
      </div>
      
      {/* ==================== TARGET PROFILE ==================== */}
      <div style={{ 
        background: COLORS.bgCard, 
        border: `1px solid ${COLORS.borderDark}`,
        borderLeft: `5px solid ${COLORS.brandRed}`,
        borderRadius: '10px',
        marginBottom: '25px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleSection('targetProfile')}
          style={{ 
            padding: '18px 25px', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: `${COLORS.brandRed}1a`
          }}
        >
          <h2 style={{ color: COLORS.brandRed, margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
            🎯 Target Profile
          </h2>
          <span style={{ color: COLORS.brandRed, fontSize: '1.2rem' }}>{expandedSections.targetProfile ? '▼' : '▶'}</span>
        </div>
        
        {expandedSections.targetProfile && (
          <div style={{ padding: '25px' }}>
            {/* Primary Target Info */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '20px',
              marginBottom: '25px'
            }}>
              <div style={{ background: COLORS.bgSecondary, padding: '18px', borderRadius: '8px' }}>
                <div style={{ color: COLORS.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Target</div>
                <div style={{ color: COLORS.neonBlue, fontSize: '1.3rem', fontWeight: '700', fontFamily: 'monospace' }}>{results.target || scanData.target}</div>
              </div>
              
              <div style={{ background: COLORS.bgSecondary, padding: '18px', borderRadius: '8px' }}>
                <div style={{ color: COLORS.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>IP Address</div>
                <div style={{ color: COLORS.neonBlue, fontSize: '1.1rem', fontFamily: 'monospace' }}>
                  {dnsResolution.aRecords?.[0] || geo.ip || networkServices.ip || 'N/A'}
                </div>
              </div>
              
              <div style={{ background: COLORS.bgSecondary, padding: '18px', borderRadius: '8px' }}>
                <div style={{ color: COLORS.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Location</div>
                <div style={{ color: COLORS.textPrimary, fontSize: '1.1rem' }}>
                  {geo.city && geo.country ? `${geo.city}, ${geo.region || ''} ${geo.country}` : 'N/A'}
                </div>
              </div>
              
              <div style={{ background: COLORS.bgSecondary, padding: '18px', borderRadius: '8px' }}>
                <div style={{ color: COLORS.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Risk Score</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ color: getSeverityColor(scanData.riskLevel || results.riskLevel), fontSize: '1.8rem', fontWeight: '900' }}>
                    {scanData.riskScore || results.riskScore || 0}
                  </span>
                  <span style={{ 
                    background: `${getSeverityColor(scanData.riskLevel || results.riskLevel)}25`,
                    color: getSeverityColor(scanData.riskLevel || results.riskLevel),
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>{scanData.riskLevel || results.riskLevel}</span>
                </div>
              </div>
            </div>
            
            {/* Statistics Summary */}
            <div style={{ 
              background: COLORS.bgSecondary, 
              borderRadius: '8px', 
              padding: '20px',
              marginBottom: '20px'
            }}>
              <div style={{ color: COLORS.brandRed, fontSize: '0.9rem', fontWeight: '600', marginBottom: '15px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Scan Statistics
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '15px' }}>
                <div style={{ textAlign: 'center', padding: '12px', background: COLORS.bgCard, borderRadius: '6px' }}>
                  <div style={{ color: COLORS.neonBlue, fontSize: '1.8rem', fontWeight: '800' }}>
                    {stats.openPorts || ports.portCount || 0}
                  </div>
                  <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>Open Ports</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: COLORS.bgCard, borderRadius: '6px' }}>
                  <div style={{ color: COLORS.neonBlue, fontSize: '1.8rem', fontWeight: '800' }}>
                    {vulnList.length || 0}
                  </div>
                  <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>Vulnerabilities</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: COLORS.bgCard, borderRadius: '6px' }}>
                  <div style={{ color: (vulnSummary.criticalCount || 0) > 0 ? COLORS.severityCritical : COLORS.neonGreen, fontSize: '1.8rem', fontWeight: '800' }}>
                    {vulnSummary.criticalCount || mergedSeverityCounts.critical || 0}
                  </div>
                  <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>Critical</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: COLORS.bgCard, borderRadius: '6px' }}>
                  <div style={{ color: COLORS.neonBlue, fontSize: '1.8rem', fontWeight: '800' }}>
                    {stats.subdomainsFound || subfinder.totalSubdomains || dnsSubdomains.totalUnique || 0}
                  </div>
                  <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>Subdomains</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: COLORS.bgCard, borderRadius: '6px' }}>
                  <div style={{ color: COLORS.neonBlue, fontSize: '1.8rem', fontWeight: '800' }}>
                    {allTechnologies.length || httpSummary.uniqueTechnologies || 0}
                  </div>
                  <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>Technologies</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: COLORS.bgCard, borderRadius: '6px' }}>
                  <div style={{ color: COLORS.neonBlue, fontSize: '1.8rem', fontWeight: '800' }}>{mitreMapping.length || 0}</div>
                  <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>MITRE Tactics</div>
                </div>
              </div>
            </div>
            
            {/* Network & Infrastructure Info */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
              gap: '12px' 
            }}>
              <div style={{ background: COLORS.bgSecondary, padding: '12px 15px', borderRadius: '6px' }}>
                <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>ISP: </span>
                <span style={{ color: COLORS.textPrimary, fontSize: '0.9rem' }}>{geo.isp || 'N/A'}</span>
              </div>
              <div style={{ background: COLORS.bgSecondary, padding: '12px 15px', borderRadius: '6px' }}>
                <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>Organization: </span>
                <span style={{ color: COLORS.textPrimary, fontSize: '0.9rem' }}>{geo.organization || 'N/A'}</span>
              </div>
              <div style={{ background: COLORS.bgSecondary, padding: '12px 15px', borderRadius: '6px' }}>
                <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>ASN: </span>
                <span style={{ color: COLORS.textPrimary, fontSize: '0.9rem' }}>{geo.asn || 'N/A'}</span>
              </div>
              <div style={{ background: COLORS.bgSecondary, padding: '12px 15px', borderRadius: '6px' }}>
                <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>Registrar: </span>
                <span style={{ color: COLORS.textPrimary, fontSize: '0.9rem' }}>{whois.registrar || 'N/A'}</span>
              </div>
              <div style={{ background: COLORS.bgSecondary, padding: '12px 15px', borderRadius: '6px' }}>
                <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>Scan Duration: </span>
                <span style={{ color: COLORS.neonGreen, fontSize: '0.9rem' }}>{formatDuration(scanData.scanDuration || results.scanDuration)}</span>
              </div>
              <div style={{ background: COLORS.bgSecondary, padding: '12px 15px', borderRadius: '6px' }}>
                <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>Timezone: </span>
                <span style={{ color: COLORS.textPrimary, fontSize: '0.9rem' }}>{geo.timezone || 'N/A'}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* ==================== ATTACK SURFACE ANALYSIS ==================== */}
      <div style={{ 
        background: COLORS.bgCard, 
        border: `1px solid ${COLORS.borderDark}`,
        borderLeft: `5px solid ${COLORS.brandRed}`,
        borderRadius: '10px',
        marginBottom: '25px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleSection('attackSurface')}
          style={{ 
            padding: '18px 25px', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: `${COLORS.brandRed}1a`
          }}
        >
          <h2 style={{ color: COLORS.brandRed, margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
            🔍 Attack Surface Analysis
          </h2>
          <span style={{ color: COLORS.brandRed, fontSize: '1.2rem' }}>{expandedSections.attackSurface ? '▼' : '▶'}</span>
        </div>
        
        {expandedSections.attackSurface && (
          <div style={{ padding: '25px' }}>
            {/* Summary Metrics */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', 
              gap: '15px',
              marginBottom: '25px'
            }}>
              <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '18px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.8rem', fontWeight: '900', color: COLORS.neonBlue }}>{ports.portCount || 0}</div>
                <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Open Ports</div>
              </div>
              <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '18px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.8rem', fontWeight: '900', color: COLORS.neonBlue }}>
                  {subfinder.totalSubdomains || dnsSubdomains.totalUnique || 0}
                </div>
                <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Subdomains</div>
              </div>
              <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '18px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.8rem', fontWeight: '900', color: COLORS.neonBlue }}>{crawling.totalEndpoints || 0}</div>
                <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Endpoints</div>
              </div>
              <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '18px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.8rem', fontWeight: '900', color: COLORS.neonBlue }}>{allTechnologies.length || 0}</div>
                <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Technologies</div>
              </div>
              <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '18px', textAlign: 'center' }}>
                <div style={{ fontSize: '1.8rem', fontWeight: '900', color: COLORS.neonBlue }}>{parameterNames.length || 0}</div>
                <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Parameters</div>
              </div>
            </div>
            
            {/* Technologies Detected */}
            {allTechnologies.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ color: COLORS.brandRed, fontSize: '0.9rem', fontWeight: '600', marginBottom: '12px' }}>Technologies Detected</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {allTechnologies.map((tech, i) => (
                    <span key={i} style={{ 
                      background: COLORS.bgSecondary, 
                      border: `1px solid ${COLORS.neonBlue}`,
                      color: COLORS.neonBlue, 
                      padding: '6px 14px', 
                      borderRadius: '15px', 
                      fontSize: '0.85rem' 
                    }}>
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Expandable Details Section */}
            <div 
              onClick={() => toggleSection('attackSurfaceDetails')}
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                cursor: 'pointer',
                padding: '12px 15px',
                background: COLORS.bgSecondary,
                borderRadius: '8px',
                marginBottom: expandedSections.attackSurfaceDetails ? '15px' : '0'
              }}
            >
              <span style={{ color: COLORS.neonBlue, fontWeight: '600' }}>
                View Detailed Breakdown
              </span>
              <span style={{ color: COLORS.neonBlue }}>{expandedSections.attackSurfaceDetails ? '▼' : '▶'}</span>
            </div>
            
            {expandedSections.attackSurfaceDetails && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                {/* Ports & Services */}
                <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px' }}>
                  <h4 style={{ color: COLORS.brandRed, marginBottom: '15px', fontSize: '1rem' }}>Open Ports & Services</h4>
                  
                  {/* High Risk Warning */}
                  {(riskClassification.high?.length > 0 || remoteAccessExposed.length > 0) && (
                    <div style={{ marginBottom: '15px', padding: '12px', background: `${COLORS.severityCritical}26`, borderRadius: '6px', borderLeft: `4px solid ${COLORS.severityCritical}` }}>
                      <div style={{ color: COLORS.severityCritical, fontWeight: '600', fontSize: '0.85rem' }}>⚠️ High-Risk Services Detected</div>
                    </div>
                  )}
                  
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {portServices.map((svc, i) => {
                      const isHighRisk = riskClassification.high?.some(h => h.port === svc.port);
                      return (
                        <div key={i} style={{ 
                          background: COLORS.bgCard, 
                          padding: '10px 12px', 
                          borderRadius: '6px',
                          borderLeft: `3px solid ${isHighRisk ? COLORS.severityCritical : COLORS.neonGreen}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <span style={{ color: COLORS.textPrimary, fontWeight: '600' }}>:{svc.port}</span>
                            <span style={{ color: COLORS.textMuted, marginLeft: '10px', fontSize: '0.85rem' }}>{svc.service}</span>
                          </div>
                          <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>{svc.protocol}</span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Service Banners */}
                  {servicesList.length > 0 && (
                    <div style={{ marginTop: '15px' }}>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.8rem', marginBottom: '8px' }}>Service Banners:</div>
                      {servicesList.map((svc, i) => (
                        <div key={i} style={{ background: '#0a0a0a', padding: '8px 12px', borderRadius: '4px', marginBottom: '5px', fontFamily: 'monospace', fontSize: '0.8rem', color: COLORS.neonGreen }}>
                          :{svc.port} - {svc.product} {svc.version ? `v${svc.version}` : ''}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* HTTP Response Info */}
                {probes.length > 0 && (
                  <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px' }}>
                    <h4 style={{ color: COLORS.brandRed, marginBottom: '15px', fontSize: '1rem' }}>HTTP Response Analysis</h4>
                    {probes.map((probe, i) => (
                      <div key={i} style={{ background: COLORS.bgCard, borderRadius: '6px', padding: '15px', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ color: COLORS.neonBlue, fontFamily: 'monospace', fontSize: '0.85rem' }}>{probe.url}</span>
                          <span style={{ 
                            background: probe.statusCode >= 200 && probe.statusCode < 300 ? `${COLORS.neonGreen}20` : `${COLORS.severityMedium}20`,
                            color: probe.statusCode >= 200 && probe.statusCode < 300 ? COLORS.neonGreen : COLORS.severityMedium,
                            padding: '3px 10px',
                            borderRadius: '10px',
                            fontSize: '0.8rem',
                            fontWeight: '600'
                          }}>{probe.statusCode}</span>
                        </div>
                        {probe.title && (
                          <div style={{ color: COLORS.textPrimary, fontSize: '0.9rem', marginBottom: '8px' }}>
                            <span style={{ color: COLORS.textMuted }}>Title: </span>{probe.title}
                          </div>
                        )}
                        {probe.server && (
                          <div style={{ color: COLORS.textMuted, fontSize: '0.85rem' }}>
                            Server: <span style={{ color: COLORS.neonBlue }}>{probe.server}</span>
                          </div>
                        )}
                        {probe.technologies?.length > 0 && (
                          <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                            {probe.technologies.map((tech, j) => (
                              <span key={j} style={{ background: `${COLORS.neonBlue}20`, color: COLORS.neonBlue, padding: '2px 8px', borderRadius: '8px', fontSize: '0.75rem' }}>
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {httpSummary && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginTop: '15px' }}>
                        <div style={{ background: COLORS.bgCard, padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                          <div style={{ color: COLORS.neonBlue, fontWeight: '700' }}>{httpSummary.liveHosts || 0}</div>
                          <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Live Hosts</div>
                        </div>
                        <div style={{ background: COLORS.bgCard, padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                          <div style={{ color: httpSummary.httpsEnabled ? COLORS.neonGreen : COLORS.severityMedium, fontWeight: '700' }}>
                            {httpSummary.httpsEnabled ? 'Yes' : 'No'}
                          </div>
                          <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>HTTPS</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Endpoint Discovery */}
                {crawlingSummary && (
                  <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px' }}>
                    <h4 style={{ color: COLORS.brandRed, marginBottom: '15px', fontSize: '1rem' }}>Endpoint Discovery</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '15px' }}>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.2rem' }}>{crawlingSummary.htmlEndpoints || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>HTML</div>
                      </div>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.2rem' }}>{crawlingSummary.jsEndpoints || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>JavaScript</div>
                      </div>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.2rem' }}>{crawlingSummary.apiEndpoints || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>API</div>
                      </div>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.2rem' }}>{crawlingSummary.uniquePaths || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Unique Paths</div>
                      </div>
                    </div>
                    
                    {parameterNames.length > 0 && (
                      <div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.8rem', marginBottom: '8px' }}>Parameters Found:</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {parameterNames.map((param, i) => (
                            <span key={i} style={{ background: COLORS.bgCard, color: COLORS.neonBlue, padding: '4px 10px', borderRadius: '8px', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                              {param}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Subdomain Categories */}
                {subfinderSummary && (
                  <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px' }}>
                    <h4 style={{ color: COLORS.brandRed, marginBottom: '15px', fontSize: '1rem' }}>Subdomain Analysis</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.2rem' }}>{subfinderSummary.wwwCount || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>WWW</div>
                      </div>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.2rem' }}>{subfinderSummary.mailCount || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Mail</div>
                      </div>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.2rem' }}>{subfinderSummary.apiCount || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>API</div>
                      </div>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.severityMedium, fontWeight: '700', fontSize: '1.2rem' }}>{subfinderSummary.adminCount || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Admin</div>
                      </div>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.severityMedium, fontWeight: '700', fontSize: '1.2rem' }}>{subfinderSummary.devCount || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Dev</div>
                      </div>
                      <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.2rem' }}>{subfinderSummary.interestingCount || 0}</div>
                        <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Interesting</div>
                      </div>
                    </div>

                    {/* Asset Monitoring Callout for large sites */}
                    {(subfinderSummary.total || subfinder.totalSubdomains || 0) > 50 && (
                      <div style={{
                        marginTop: '15px',
                        background: 'rgba(255, 184, 0, 0.15)',
                        border: '1px solid rgba(255, 184, 0, 0.3)',
                        borderRadius: '8px',
                        padding: '15px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}>
                        <span style={{ fontSize: '1.2rem' }}>🎯</span>
                        <div>
                          <div style={{ color: '#ffc107', fontWeight: '600', marginBottom: '5px' }}>
                            {subfinderSummary.total || subfinder.totalSubdomains || 0} subdomains discovered
                          </div>
                          <div style={{ color: COLORS.textMuted, fontSize: '0.85rem', lineHeight: '1.4' }}>
                            Deep Scan focuses vulnerability scanning on your main domain for reliable, quality results.
                            For comprehensive subdomain vulnerability scanning, upgrade to{' '}
                            <span style={{ color: COLORS.brandRed, fontWeight: '600' }}>Asset Monitoring</span>{' '}
                            (Pro/Enterprise tier).
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== DriftAlarm BRIEF ==================== */}
      <div style={{ 
        background: COLORS.bgCard, 
        border: `1px solid ${COLORS.borderDark}`,
        borderLeft: `5px solid ${COLORS.brandRed}`,
        borderRadius: '10px',
        marginBottom: '25px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleSection('r3dteamBrief')}
          style={{ 
            padding: '18px 25px', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: `${COLORS.brandRed}1a`
          }}
        >
          <h2 style={{ color: COLORS.brandRed, margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
            🔴 DriftAlarm Brief
          </h2>
          <span style={{ color: COLORS.brandRed, fontSize: '1.2rem' }}>{expandedSections.r3dteamBrief ? '▼' : '▶'}</span>
        </div>
        
        {expandedSections.r3dteamBrief && (
          <div style={{ padding: '25px' }}>
            {/* Threat Level & Confidence */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '25px', flexWrap: 'wrap' }}>
              <div style={{ 
                background: `${getSeverityColor(ai.overallThreatLevel)}15`,
                border: `2px solid ${getSeverityColor(ai.overallThreatLevel)}`,
                borderRadius: '10px',
                padding: '15px 25px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px'
              }}>
                <span style={{ color: COLORS.textMuted, fontSize: '0.85rem' }}>Threat Level:</span>
                <span style={{ color: getSeverityColor(ai.overallThreatLevel), fontWeight: '800', fontSize: '1.2rem', textTransform: 'uppercase' }}>
                  {ai.overallThreatLevel || scanData.riskLevel || 'N/A'}
                </span>
              </div>
              
              {ai.confidenceScore && (
                <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '15px 25px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <span style={{ color: COLORS.textMuted, fontSize: '0.85rem' }}>AI Confidence:</span>
                  <span style={{ color: COLORS.neonGreen, fontWeight: '700', fontSize: '1.2rem' }}>{ai.confidenceScore}%</span>
                </div>
              )}
              
              {ai.attackComplexity && (
                <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '15px 25px', display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <span style={{ color: COLORS.textMuted, fontSize: '0.85rem' }}>Attack Complexity:</span>
                  <span style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.2rem' }}>{ai.attackComplexity}</span>
                </div>
              )}
            </div>
            
            {/* Executive Summary */}
            {ai.executiveSummary && (
              <div style={{ marginBottom: '25px' }}>
                <h4 style={{ color: COLORS.brandRed, marginBottom: '12px', fontSize: '1rem' }}>Executive Summary</h4>
                <p style={{ color: COLORS.textPrimary, lineHeight: '1.8', fontSize: '1rem', margin: 0, background: COLORS.bgSecondary, padding: '20px', borderRadius: '8px' }}>
                  {ai.executiveSummary}
                </p>
              </div>
            )}
            
            {/* Key Findings from AI Attack Surface */}
            {aiAttackSurface.misconfigurations?.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: COLORS.brandRed, marginBottom: '12px', fontSize: '1rem' }}>Misconfigurations Identified</h4>
                {aiAttackSurface.misconfigurations.map((finding, i) => (
                  <div key={i} style={{ 
                    padding: '12px 16px', 
                    marginBottom: '10px', 
                    background: COLORS.bgSecondary, 
                    borderLeft: `3px solid ${COLORS.severityMedium}`,
                    borderRadius: '4px',
                    color: COLORS.textPrimary,
                    fontSize: '0.95rem'
                  }}>
                    {finding}
                  </div>
                ))}
              </div>
            )}
            
            {/* Information Leakage */}
            {aiAttackSurface.informationLeakage?.length > 0 && (
              <div>
                <h4 style={{ color: COLORS.brandRed, marginBottom: '12px', fontSize: '1rem' }}>Information Leakage</h4>
                {aiAttackSurface.informationLeakage.map((finding, i) => (
                  <div key={i} style={{ 
                    padding: '12px 16px', 
                    marginBottom: '10px', 
                    background: COLORS.bgSecondary, 
                    borderLeft: `3px solid ${COLORS.brandRed}`,
                    borderRadius: '4px',
                    color: COLORS.textPrimary,
                    fontSize: '0.95rem'
                  }}>
                    {finding}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* ==================== VULNERABILITIES (Collapsible) ==================== */}
      <div style={{ 
        background: COLORS.bgCard, 
        border: `1px solid ${COLORS.borderDark}`,
        borderLeft: `5px solid ${COLORS.brandRed}`,
        borderRadius: '10px',
        marginBottom: '25px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleSection('vulnerabilities')}
          style={{ 
            padding: '18px 25px', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: `${COLORS.brandRed}1a`
          }}
        >
          <h2 style={{ color: COLORS.brandRed, margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
            ⚡ Vulnerabilities ({vulnList.length || 0})
          </h2>
          <span style={{ color: COLORS.brandRed, fontSize: '1.2rem' }}>{expandedSections.vulnerabilities ? '▼' : '▶'}</span>
        </div>
        
        {expandedSections.vulnerabilities && (
          <div style={{ padding: '25px' }}>
            {/* Severity Summary */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '25px', flexWrap: 'wrap' }}>
              {[
                { label: 'Critical', count: mergedSeverityCounts.critical || 0, color: COLORS.severityCritical },
                { label: 'High', count: mergedSeverityCounts.high || 0, color: COLORS.severityHigh },
                { label: 'Medium', count: mergedSeverityCounts.medium || 0, color: COLORS.severityMedium },
                { label: 'Low', count: mergedSeverityCounts.low || 0, color: COLORS.severityLow },
                { label: 'Info', count: mergedSeverityCounts.info || 0, color: COLORS.severityInfo }
              ].map((sev, i) => (
                <div key={i} style={{ 
                  background: `${sev.color}12`, 
                  border: `2px solid ${sev.color}`,
                  borderRadius: '8px',
                  padding: '12px 20px',
                  textAlign: 'center',
                  minWidth: '80px'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '800', color: sev.color }}>{sev.count}</div>
                  <div style={{ color: sev.color, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: '600' }}>{sev.label}</div>
                </div>
              ))}
            </div>
            
            {/* CVE List */}
            {cveList.length > 0 && (
              <div style={{ marginBottom: '20px', padding: '15px', background: COLORS.bgSecondary, borderRadius: '8px' }}>
                <div style={{ color: COLORS.severityCritical, fontWeight: '600', marginBottom: '10px' }}>CVEs Identified ({cveList.length}):</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {cveList.map((cve, i) => (
                    <span key={i} style={{ 
                      background: `${COLORS.severityCritical}15`,
                      border: `1px solid ${COLORS.severityCritical}`,
                      color: COLORS.severityCritical,
                      padding: '5px 12px',
                      borderRadius: '15px',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem'
                    }}>
                      {cve}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Vulnerability Details */}
            {sortedVulns.length > 0 ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ color: COLORS.brandRed, margin: 0 }}>
                    Findings by Severity {showAllVulns ? '(showing 15)' : '(showing 5)'}
                  </h4>
                  {sortedVulns.length > 5 && (
                    <button 
                      onClick={() => setShowAllVulns(!showAllVulns)}
                      style={{ 
                        background: COLORS.neonGreen, 
                        border: 'none', 
                        borderRadius: '6px', 
                        padding: '8px 16px', 
                        color: '#000', 
                        fontWeight: '600', 
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      {showAllVulns ? 'Show Less' : `Show More (${Math.min(15, sortedVulns.length)})`}
                    </button>
                  )}
                </div>
                
                {displayVulns.map((vuln, i) => (
                  <div
                    key={i}
                    onClick={() => setExpandedVulns(prev => ({ ...prev, [i]: !prev[i] }))}
                    style={{
                      background: COLORS.bgSecondary,
                      borderLeft: `4px solid ${getSeverityColor(vuln.severity)}`,
                      borderRadius: '8px',
                      padding: '18px',
                      marginBottom: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                      <div>
                        <h5 style={{ color: COLORS.textPrimary, margin: '0 0 5px 0', fontSize: '1rem' }}>{vuln.name}</h5>
                        <span style={{ color: COLORS.neonBlue, fontFamily: 'monospace', fontSize: '0.85rem' }}>{vuln.templateId}</span>
                        {vuln.source === 'nikto' && (
                          <span style={{ marginLeft: '10px', background: COLORS.neonBlue + '20', color: COLORS.neonBlue, padding: '2px 8px', borderRadius: '10px', fontSize: '0.7rem', fontWeight: '600' }}>NIKTO</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{
                          background: `${getSeverityColor(vuln.severity)}20`,
                          color: getSeverityColor(vuln.severity),
                          padding: '5px 14px',
                          borderRadius: '15px',
                          fontSize: '0.8rem',
                          fontWeight: '700',
                          textTransform: 'uppercase'
                        }}>
                          {vuln.severity}
                        </span>
                        <span aria-hidden="true" style={{ color: COLORS.textMuted, fontSize: '0.9rem' }}>
                          {expandedVulns[i] ? '▼' : '▶'}
                        </span>
                      </div>
                    </div>

                    {vuln.description && (
                      <p style={{ color: COLORS.textMuted, fontSize: '0.9rem', lineHeight: '1.6', margin: '0 0 12px 0' }}>
                        {vuln.description.length > 200 ? vuln.description.substring(0, 200) + '...' : vuln.description}
                      </p>
                    )}

                    {vuln.matched && (
                      <div style={{ background: '#0a0a0a', padding: '10px 14px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '0.8rem', color: COLORS.neonGreen, overflow: 'auto' }}>
                        <span style={{ color: COLORS.textMuted }}>Matched: </span>{vuln.matched}
                      </div>
                    )}

                    {vuln.tags?.length > 0 && (
                      <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {vuln.tags.slice(0, 6).map((tag, j) => (
                          <span key={j} style={{ background: COLORS.bgCard, color: COLORS.textMuted, padding: '3px 8px', borderRadius: '8px', fontSize: '0.7rem' }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {expandedVulns[i] && (
                      <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '14px', cursor: 'default' }}>
                        <HttpTransactionView
                          httpTransaction={parseHttpTransactionFromRaw(vuln.raw, vuln.matched)}
                          matcherPayload={vuln.matcherName || vuln.extractedResults?.[0]}
                          showEmpty
                        />
                      </div>
                    )}
                  </div>
                ))}
                
                {sortedVulns.length > 15 && showAllVulns && (
                  <div style={{ textAlign: 'center', padding: '15px', color: COLORS.textMuted, fontSize: '0.9rem' }}>
                    Showing 15 of {sortedVulns.length} vulnerabilities. See Full JSON for complete list.
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', background: `${COLORS.neonGreen}10`, borderRadius: '10px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '10px' }}>✓</div>
                <div style={{ color: COLORS.neonGreen, fontSize: '1.1rem', fontWeight: '600' }}>No Critical Vulnerabilities Detected</div>
                <div style={{ color: COLORS.textMuted, marginTop: '5px' }}>Target shows good baseline security posture</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== CLOUD INFRASTRUCTURE ==================== */}
      {hasCloudFindings && (
        <div style={{
          background: COLORS.bgCard,
          border: `1px solid ${COLORS.borderDark}`,
          borderLeft: '5px solid #00A8FF',
          borderRadius: '10px',
          marginBottom: '25px',
          overflow: 'hidden'
        }}>
          <div
            onClick={() => toggleSection('cloudInfrastructure')}
            style={{
              padding: '18px 25px',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: 'rgba(0,217,255,0.1)'
            }}
          >
            <h2 style={{ color: '#00A8FF', margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
              ☁️ Cloud Infrastructure
            </h2>
            <span style={{ color: '#00A8FF', fontSize: '1.2rem' }}>{expandedSections.cloudInfrastructure ? '▼' : '▶'}</span>
          </div>

          {expandedSections.cloudInfrastructure && (
            <div style={{ padding: '25px' }}>
              {/* Provider Detection Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', marginBottom: '25px' }}>
                {/* AWS */}
                <div style={{
                  background: cloudProviders.aws.detected ? 'rgba(255,153,0,0.15)' : COLORS.bgSecondary,
                  border: `1px solid ${cloudProviders.aws.detected ? '#FF9900' : COLORS.borderDark}`,
                  borderRadius: '10px',
                  padding: '18px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>🟠</div>
                  <div style={{ color: cloudProviders.aws.detected ? '#FF9900' : COLORS.textMuted, fontWeight: '700', fontSize: '0.9rem' }}>
                    AWS {cloudProviders.aws.detected ? 'DETECTED' : 'NOT FOUND'}
                  </div>
                  {cloudProviders.aws.buckets?.length > 0 && (
                    <div style={{ color: COLORS.textMuted, marginTop: '5px', fontSize: '0.8rem' }}>
                      {cloudProviders.aws.buckets.length} bucket(s)
                    </div>
                  )}
                </div>

                {/* Azure */}
                <div style={{
                  background: cloudProviders.azure.detected ? 'rgba(0,120,212,0.15)' : COLORS.bgSecondary,
                  border: `1px solid ${cloudProviders.azure.detected ? '#0078D4' : COLORS.borderDark}`,
                  borderRadius: '10px',
                  padding: '18px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>🔵</div>
                  <div style={{ color: cloudProviders.azure.detected ? '#0078D4' : COLORS.textMuted, fontWeight: '700', fontSize: '0.9rem' }}>
                    AZURE {cloudProviders.azure.detected ? 'DETECTED' : 'NOT FOUND'}
                  </div>
                  {cloudProviders.azure.tenantId && (
                    <div style={{ color: COLORS.textMuted, marginTop: '5px', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                      Tenant ID found
                    </div>
                  )}
                </div>

                {/* GCP */}
                <div style={{
                  background: cloudProviders.gcp.detected ? 'rgba(66,133,244,0.15)' : COLORS.bgSecondary,
                  border: `1px solid ${cloudProviders.gcp.detected ? '#4285F4' : COLORS.borderDark}`,
                  borderRadius: '10px',
                  padding: '18px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: '8px' }}>🔴</div>
                  <div style={{ color: cloudProviders.gcp.detected ? '#4285F4' : COLORS.textMuted, fontWeight: '700', fontSize: '0.9rem' }}>
                    GCP {cloudProviders.gcp.detected ? 'DETECTED' : 'NOT FOUND'}
                  </div>
                  {cloudProviders.gcp.buckets?.length > 0 && (
                    <div style={{ color: COLORS.textMuted, marginTop: '5px', fontSize: '0.8rem' }}>
                      {cloudProviders.gcp.buckets.length} bucket(s)
                    </div>
                  )}
                </div>
              </div>

              {/* Azure Tenant ID */}
              {cloudProviders.azure.tenantId && (
                <div style={{ marginBottom: '20px', padding: '15px', background: 'rgba(0,120,212,0.15)', borderRadius: '8px', borderLeft: '4px solid #0078D4' }}>
                  <div style={{ color: '#0078D4', fontWeight: '600', marginBottom: '8px', fontSize: '0.9rem' }}>Azure Tenant ID Discovered</div>
                  <code style={{
                    fontFamily: 'monospace',
                    fontSize: '0.95rem',
                    color: COLORS.textPrimary,
                    background: COLORS.bgSecondary,
                    padding: '8px 12px',
                    borderRadius: '4px',
                    display: 'inline-block'
                  }}>
                    {cloudProviders.azure.tenantId}
                  </code>
                  <p style={{ color: COLORS.textMuted, fontSize: '0.8rem', marginTop: '10px', marginBottom: 0 }}>
                    This tenant ID can be used for Azure AD enumeration and reconnaissance.
                  </p>
                </div>
              )}

              {/* Cloud Findings from Nuclei */}
              {cloudFindings.length > 0 && (
                <div>
                  <h4 style={{ color: '#00A8FF', marginBottom: '12px', fontSize: '1rem' }}>Cloud Security Findings ({cloudFindings.length})</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {cloudFindings.slice(0, 10).map((finding, i) => (
                      <div key={i} style={{
                        padding: '12px 16px',
                        background: COLORS.bgSecondary,
                        borderLeft: `3px solid ${getSeverityColor(finding.severity)}`,
                        borderRadius: '4px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <span style={{
                            background: `${getSeverityColor(finding.severity)}25`,
                            color: getSeverityColor(finding.severity),
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: '600',
                            marginRight: '10px',
                            textTransform: 'uppercase'
                          }}>{finding.severity}</span>
                          <span style={{ color: COLORS.textPrimary, fontSize: '0.9rem' }}>{finding.name || finding.templateId}</span>
                        </div>
                        {finding.matchedAt && (
                          <span style={{ color: COLORS.textMuted, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                            {finding.matchedAt.slice(0, 50)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No findings message */}
              {!cloudProviders.azure.tenantId && cloudFindings.length === 0 && (
                <p style={{ color: COLORS.textMuted, fontStyle: 'italic', margin: 0 }}>
                  Cloud provider detection completed. Results shown above.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ==================== ATTACK INTELLIGENCE (SCRUTINIZE-specific) ==================== */}
      <div style={{
        background: COLORS.bgCard,
        border: `1px solid ${COLORS.borderDark}`,
        borderLeft: `5px solid ${COLORS.brandRed}`,
        borderRadius: '10px',
        marginBottom: '25px',
        overflow: 'hidden'
      }}>
        <div
          onClick={() => toggleSection('attackIntelligence')}
          style={{ 
            padding: '18px 25px', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: `${COLORS.brandRed}1a`
          }}
        >
          <h2 style={{ color: COLORS.brandRed, margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
            🎯 Attack Intelligence
          </h2>
          <span style={{ color: COLORS.brandRed, fontSize: '1.2rem' }}>{expandedSections.attackIntelligence ? '▼' : '▶'}</span>
        </div>
        
        {expandedSections.attackIntelligence && (
          <div style={{ padding: '25px' }}>
            {/* MITRE ATT&CK Summary */}
            {mitreMapping.length > 0 && (
              <div style={{ marginBottom: '25px' }}>
                <div 
                  onClick={(e) => { e.stopPropagation(); toggleSection('mitreDetails'); }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}
                >
                  <h4 style={{ color: COLORS.brandRed, margin: 0, fontSize: '1rem' }}>
                    MITRE ATT&CK Mapping ({mitreMapping.length} techniques)
                  </h4>
                  <span style={{ color: COLORS.brandRed }}>{expandedSections.mitreDetails ? '▼' : '▶'}</span>
                </div>
                
                {/* Tactic Summary Badges */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
                  {[...new Set(mitreMapping.map(m => m.tactic))].map((tactic, i) => (
                    <span key={i} style={{ 
                      background: COLORS.bgSecondary, 
                      border: `1px solid ${COLORS.neonBlue}`,
                      color: COLORS.neonBlue, 
                      padding: '6px 14px', 
                      borderRadius: '15px', 
                      fontSize: '0.85rem' 
                    }}>
                      {tactic}
                    </span>
                  ))}
                </div>
                
                {expandedSections.mitreDetails && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
                    {mitreMapping.map((technique, i) => (
                      <div key={i} style={{ background: COLORS.bgSecondary, borderRadius: '8px', padding: '15px', borderLeft: `3px solid ${COLORS.neonBlue}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                          <span style={{ color: COLORS.neonBlue, fontWeight: '700', fontFamily: 'monospace' }}>{technique.technique}</span>
                          <span style={{ color: COLORS.textMuted, fontSize: '0.8rem' }}>{technique.tactic}</span>
                        </div>
                        <div style={{ color: COLORS.textPrimary, fontWeight: '600', marginBottom: '8px' }}>{technique.name}</div>
                        {technique.evidence && (
                          <div style={{ color: COLORS.textMuted, fontSize: '0.85rem', lineHeight: '1.5', marginBottom: '10px' }}>
                            {technique.evidence.length > 150 ? technique.evidence.substring(0, 150) + '...' : technique.evidence}
                          </div>
                        )}
                        {technique.exploitability && (
                          <span style={{ 
                            background: technique.exploitability === 'High' ? `${COLORS.severityCritical}20` : `${COLORS.severityMedium}20`,
                            color: technique.exploitability === 'High' ? COLORS.severityCritical : COLORS.severityMedium,
                            padding: '3px 10px',
                            borderRadius: '10px',
                            fontSize: '0.75rem'
                          }}>
                            {technique.exploitability} Exploitability
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Penetration Testing Roadmap */}
            {(pentestRoadmap.phase1_reconnaissance || pentestRoadmap.phase2_scanning || pentestRoadmap.phase3_exploitation || pentestRoadmap.phase4_postExploitation) && (
              <div>
                <div 
                  onClick={(e) => { e.stopPropagation(); toggleSection('pentestRoadmap'); }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}
                >
                  <h4 style={{ color: COLORS.brandRed, margin: 0, fontSize: '1rem' }}>
                    Penetration Testing Roadmap
                  </h4>
                  <span style={{ color: COLORS.brandRed }}>{expandedSections.pentestRoadmap ? '▼' : '▶'}</span>
                </div>
                
                {/* Phase Timeline Visual */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', marginBottom: '20px' }}>
                  {[1, 2, 3, 4].map((phase) => (
                    <div key={phase} style={{ textAlign: 'center' }}>
                      <div style={{ 
                        width: '50px', 
                        height: '50px', 
                        borderRadius: '50%', 
                        background: `${getPhaseColor(phase)}20`,
                        border: `3px solid ${getPhaseColor(phase)}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: getPhaseColor(phase),
                        fontWeight: '800',
                        fontSize: '1.2rem',
                        marginBottom: '8px'
                      }}>{phase}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                        {['Recon', 'Scan', 'Exploit', 'Post'][phase - 1]}
                      </div>
                    </div>
                  ))}
                </div>
                
                {expandedSections.pentestRoadmap && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                    {[
                      { phase: 1, name: 'Reconnaissance', data: pentestRoadmap.phase1_reconnaissance },
                      { phase: 2, name: 'Scanning', data: pentestRoadmap.phase2_scanning },
                      { phase: 3, name: 'Exploitation', data: pentestRoadmap.phase3_exploitation },
                      { phase: 4, name: 'Post-Exploitation', data: pentestRoadmap.phase4_postExploitation }
                    ].filter(p => p.data).map((phaseData) => (
                      <div key={phaseData.phase} style={{ 
                        background: COLORS.bgSecondary, 
                        borderRadius: '8px', 
                        padding: '18px',
                        borderTop: `4px solid ${getPhaseColor(phaseData.phase)}`
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <span style={{ 
                            background: getPhaseColor(phaseData.phase),
                            color: '#000',
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '800'
                          }}>{phaseData.phase}</span>
                          <span style={{ color: getPhaseColor(phaseData.phase), fontWeight: '700' }}>{phaseData.name}</span>
                        </div>
                        
                        {phaseData.data.completed?.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ color: COLORS.neonGreen, fontSize: '0.75rem', marginBottom: '5px' }}>✓ Completed:</div>
                            {phaseData.data.completed.slice(0, 3).map((item, j) => (
                              <div key={j} style={{ color: COLORS.textMuted, fontSize: '0.85rem', marginBottom: '3px' }}>• {item}</div>
                            ))}
                          </div>
                        )}
                        
                        {phaseData.data.nextSteps?.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ color: COLORS.severityMedium, fontSize: '0.75rem', marginBottom: '5px' }}>Next Steps:</div>
                            {phaseData.data.nextSteps.slice(0, 3).map((item, j) => (
                              <div key={j} style={{ color: COLORS.textPrimary, fontSize: '0.85rem', marginBottom: '3px' }}>• {item}</div>
                            ))}
                          </div>
                        )}
                        
                        {phaseData.data.primaryTargets?.length > 0 && (
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ color: COLORS.severityCritical, fontSize: '0.75rem', marginBottom: '5px' }}>Primary Targets:</div>
                            {phaseData.data.primaryTargets.slice(0, 3).map((item, j) => (
                              <div key={j} style={{ color: COLORS.textPrimary, fontSize: '0.85rem', marginBottom: '3px' }}>• {item}</div>
                            ))}
                          </div>
                        )}
                        
                        {phaseData.data.tools?.length > 0 && (
                          <div>
                            <div style={{ color: COLORS.textMuted, fontSize: '0.75rem', marginBottom: '5px' }}>Tools:</div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                              {phaseData.data.tools.slice(0, 4).map((tool, j) => (
                                <span key={j} style={{ 
                                  background: `${getPhaseColor(phaseData.phase)}15`,
                                  color: getPhaseColor(phaseData.phase),
                                  padding: '3px 8px',
                                  borderRadius: '8px',
                                  fontSize: '0.7rem',
                                  fontFamily: 'monospace'
                                }}>{tool.split('/').pop()}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* No data message */}
            {mitreMapping.length === 0 && !pentestRoadmap.phase1_reconnaissance && (
              <div style={{ textAlign: 'center', padding: '30px', color: COLORS.textMuted }}>
                No attack intelligence data available for this scan.
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* ==================== DEFENSIVE POSTURE & RECOMMENDATIONS ==================== */}
      <div style={{ 
        background: COLORS.bgCard, 
        border: `1px solid ${COLORS.borderDark}`,
        borderLeft: `5px solid ${COLORS.neonGreen}`,
        borderRadius: '10px',
        marginBottom: '25px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleSection('defensivePosture')}
          style={{ 
            padding: '18px 25px', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: 'rgba(57,255,20,0.15)'
          }}
        >
          <h2 style={{ color: COLORS.neonGreen, margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
            🛡️ Defensive Posture & Recommendations
          </h2>
          <span style={{ color: COLORS.neonGreen, fontSize: '1.2rem' }}>{expandedSections.defensivePosture ? '▼' : '▶'}</span>
        </div>
        
        {expandedSections.defensivePosture && (
          <div style={{ padding: '25px' }}>
            {/* Defense Status Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '25px' }}>
              {/* WAF Status */}
              <div style={{ 
                background: waf.wafDetected ? `${COLORS.neonGreen}15` : `${COLORS.severityCritical}15`,
                border: `2px solid ${waf.wafDetected ? COLORS.neonGreen : COLORS.severityCritical}`,
                borderRadius: '10px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{waf.wafDetected ? '🛡️' : '⚠️'}</div>
                <div style={{ color: waf.wafDetected ? COLORS.neonGreen : COLORS.severityCritical, fontWeight: '700', fontSize: '1rem' }}>
                  WAF {waf.wafDetected ? 'ACTIVE' : 'NOT DETECTED'}
                </div>
                {waf.wafName && <div style={{ color: COLORS.textPrimary, marginTop: '5px', fontSize: '0.9rem' }}>{waf.wafName}</div>}
              </div>
              
              {/* HTTPS Status */}
              <div style={{ 
                background: httpSummary.httpsEnabled ? `${COLORS.neonGreen}15` : `${COLORS.severityMedium}15`,
                border: `2px solid ${httpSummary.httpsEnabled ? COLORS.neonGreen : COLORS.severityMedium}`,
                borderRadius: '10px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{httpSummary.httpsEnabled ? '🔒' : '🔓'}</div>
                <div style={{ color: httpSummary.httpsEnabled ? COLORS.neonGreen : COLORS.severityMedium, fontWeight: '700', fontSize: '1rem' }}>
                  HTTPS {httpSummary.httpsEnabled ? 'ENABLED' : 'CHECK NEEDED'}
                </div>
              </div>
              
              {/* Exposed Services */}
              <div style={{ 
                background: (remoteAccessExposed.length > 0 || databasesExposed.length > 0) ? `${COLORS.severityCritical}15` : `${COLORS.neonGreen}15`,
                border: `2px solid ${(remoteAccessExposed.length > 0 || databasesExposed.length > 0) ? COLORS.severityCritical : COLORS.neonGreen}`,
                borderRadius: '10px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '8px' }}>
                  {(remoteAccessExposed.length > 0 || databasesExposed.length > 0) ? '🚨' : '✓'}
                </div>
                <div style={{ color: (remoteAccessExposed.length > 0 || databasesExposed.length > 0) ? COLORS.severityCritical : COLORS.neonGreen, fontWeight: '700', fontSize: '1rem' }}>
                  {(remoteAccessExposed.length > 0 || databasesExposed.length > 0) ? 'EXPOSURE RISK' : 'SERVICES OK'}
                </div>
                {remoteAccessExposed.length > 0 && (
                  <div style={{ color: COLORS.textMuted, marginTop: '5px', fontSize: '0.85rem' }}>
                    {remoteAccessExposed.map(s => `${s.service}:${s.port}`).join(', ')}
                  </div>
                )}
              </div>
            </div>
            
            {/* Risk Factors */}
            {(scanData.riskFactors?.length > 0 || execSummary.riskFactors?.length > 0) && (
              <div style={{ marginBottom: '25px', padding: '15px', background: COLORS.bgSecondary, borderRadius: '8px' }}>
                <div style={{ color: COLORS.brandRed, fontWeight: '600', marginBottom: '10px' }}>Risk Factors Identified:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {(scanData.riskFactors || execSummary.riskFactors || []).map((factor, i) => (
                    <span key={i} style={{ 
                      background: `${COLORS.severityMedium}20`,
                      color: COLORS.severityMedium,
                      padding: '6px 12px',
                      borderRadius: '15px',
                      fontSize: '0.85rem'
                    }}>
                      {factor}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Compliance Mapping */}
            {(complianceMapping.owaspTop10?.length > 0 || complianceMapping.cisControls?.length > 0 || complianceMapping.nistCsf?.length > 0) && (
              <div style={{ marginBottom: '25px' }}>
                <div 
                  onClick={(e) => { e.stopPropagation(); toggleSection('compliance'); }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}
                >
                  <h4 style={{ color: COLORS.neonGreen, margin: 0, fontSize: '1rem' }}>
                    Compliance Mapping
                  </h4>
                  <span style={{ color: COLORS.neonGreen }}>{expandedSections.compliance ? '▼' : '▶'}</span>
                </div>
                
                {expandedSections.compliance && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                    {complianceMapping.owaspTop10?.length > 0 && (
                      <div style={{ background: COLORS.bgSecondary, borderRadius: '8px', padding: '18px' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', marginBottom: '12px' }}>OWASP Top 10</div>
                        {complianceMapping.owaspTop10.slice(0, 4).map((item, i) => (
                          <div key={i} style={{ marginBottom: '10px', padding: '10px', background: COLORS.bgCard, borderRadius: '4px' }}>
                            <div style={{ color: COLORS.textPrimary, fontSize: '0.85rem', fontWeight: '600' }}>{item.category}</div>
                            {item.findings?.slice(0, 2).map((finding, j) => (
                              <div key={j} style={{ color: COLORS.textMuted, fontSize: '0.8rem', marginTop: '5px' }}>• {finding}</div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {complianceMapping.cisControls?.length > 0 && (
                      <div style={{ background: COLORS.bgSecondary, borderRadius: '8px', padding: '18px' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', marginBottom: '12px' }}>CIS Controls</div>
                        {complianceMapping.cisControls.slice(0, 4).map((control, i) => (
                          <div key={i} style={{ marginBottom: '10px', padding: '10px', background: COLORS.bgCard, borderRadius: '4px' }}>
                            <div style={{ color: COLORS.textPrimary, fontSize: '0.85rem', fontWeight: '600' }}>{control.control}</div>
                            {control.gaps?.slice(0, 2).map((gap, j) => (
                              <div key={j} style={{ color: COLORS.textMuted, fontSize: '0.8rem', marginTop: '5px' }}>• {gap}</div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {complianceMapping.nistCsf?.length > 0 && (
                      <div style={{ background: COLORS.bgSecondary, borderRadius: '8px', padding: '18px' }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', marginBottom: '12px' }}>NIST CSF</div>
                        {complianceMapping.nistCsf.slice(0, 4).map((item, i) => (
                          <div key={i} style={{ marginBottom: '10px', padding: '10px', background: COLORS.bgCard, borderRadius: '4px' }}>
                            <div style={{ color: COLORS.textPrimary, fontSize: '0.85rem', fontWeight: '600' }}>{item.function} - {item.category}</div>
                            {item.gaps?.slice(0, 2).map((gap, j) => (
                              <div key={j} style={{ color: COLORS.textMuted, fontSize: '0.8rem', marginTop: '5px' }}>• {gap}</div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Threat Intelligence */}
            {(threatIntel.knownExploits?.length > 0 || threatIntel.threatActors?.length > 0 || threatIntel.recentCampaigns?.length > 0) && (
              <div style={{ marginBottom: '25px' }}>
                <div 
                  onClick={(e) => { e.stopPropagation(); toggleSection('threatIntel'); }}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}
                >
                  <h4 style={{ color: COLORS.brandRed, margin: 0, fontSize: '1rem' }}>
                    Threat Intelligence
                  </h4>
                  <span style={{ color: COLORS.brandRed }}>{expandedSections.threatIntel ? '▼' : '▶'}</span>
                </div>
                
                {expandedSections.threatIntel && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
                    {threatIntel.knownExploits?.length > 0 && (
                      <div style={{ background: COLORS.bgSecondary, borderRadius: '8px', padding: '18px', borderLeft: `3px solid ${COLORS.severityCritical}` }}>
                        <div style={{ color: COLORS.severityCritical, fontWeight: '700', marginBottom: '12px' }}>Known Exploits</div>
                        {threatIntel.knownExploits.map((exploit, i) => (
                          <div key={i} style={{ color: COLORS.textPrimary, fontSize: '0.85rem', marginBottom: '8px' }}>
                            • {typeof exploit === 'string' ? exploit : exploit.name || exploit}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {threatIntel.threatActors?.length > 0 && (
                      <div style={{ background: COLORS.bgSecondary, borderRadius: '8px', padding: '18px', borderLeft: `3px solid ${COLORS.brandRed}` }}>
                        <div style={{ color: COLORS.brandRed, fontWeight: '700', marginBottom: '12px' }}>Threat Actors</div>
                        {threatIntel.threatActors.map((actor, i) => (
                          <div key={i} style={{ color: COLORS.textPrimary, fontSize: '0.85rem', marginBottom: '8px' }}>
                            • {typeof actor === 'string' ? actor : actor.name || actor}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {threatIntel.recentCampaigns?.length > 0 && (
                      <div style={{ background: COLORS.bgSecondary, borderRadius: '8px', padding: '18px', borderLeft: `3px solid ${COLORS.neonBlue}` }}>
                        <div style={{ color: COLORS.neonBlue, fontWeight: '700', marginBottom: '12px' }}>Recent Campaigns</div>
                        {threatIntel.recentCampaigns.map((campaign, i) => (
                          <div key={i} style={{ color: COLORS.textPrimary, fontSize: '0.85rem', marginBottom: '8px' }}>
                            • {typeof campaign === 'string' ? campaign : campaign.name || campaign}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Prioritized Recommendations */}
            {prioritizedRecs.length > 0 && (
              <div>
                <div 
                  onClick={(e) => { e.stopPropagation(); toggleSection('recommendations'); }}
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    marginBottom: '15px'
                  }}
                >
                  <h4 style={{ color: COLORS.neonGreen, margin: 0, fontSize: '1rem' }}>
                    Prioritized Recommendations ({prioritizedRecs.length})
                  </h4>
                  <span style={{ color: COLORS.neonGreen }}>{expandedSections.recommendations ? '▼' : '▶'}</span>
                </div>
                
                {expandedSections.recommendations && prioritizedRecs.map((rec, i) => (
                  <div key={i} style={{ 
                    background: COLORS.bgSecondary,
                    borderLeft: `4px solid ${getSeverityColor(rec.priority)}`,
                    borderRadius: '8px',
                    padding: '18px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <span style={{ 
                        background: getSeverityColor(rec.priority),
                        color: rec.priority?.toLowerCase() === 'low' || rec.priority?.toLowerCase() === 'info' ? '#000' : '#fff',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '0.9rem'
                      }}>{rec.rank}</span>
                      <span style={{ 
                        background: `${getSeverityColor(rec.priority)}20`,
                        color: getSeverityColor(rec.priority),
                        padding: '4px 12px',
                        borderRadius: '10px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>{rec.priority}</span>
                      <span style={{ color: COLORS.textMuted, fontSize: '0.85rem' }}>
                        {rec.effort} effort • {rec.impact} impact • {rec.timeline}
                      </span>
                    </div>
                    <div style={{ color: COLORS.textPrimary, fontWeight: '600', marginBottom: '8px' }}>{rec.vulnerability}</div>
                    {rec.attackVector && (
                      <div style={{ color: COLORS.textMuted, fontSize: '0.9rem', marginBottom: '10px' }}>
                        <span style={{ color: COLORS.severityMedium }}>Attack Vector: </span>{rec.attackVector}
                      </div>
                    )}
                    <p style={{ color: COLORS.textPrimary, fontSize: '0.95rem', margin: 0, lineHeight: '1.7' }}>
                      {rec.remediation}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* ==================== TOOL SUMMARIES ==================== */}
      <div style={{ 
        background: COLORS.bgCard, 
        border: `1px solid ${COLORS.borderDark}`,
        borderRadius: '10px',
        marginBottom: '25px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleSection('toolSummaries')}
          style={{ 
            padding: '18px 25px', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: 'rgba(255,255,255,0.03)'
          }}
        >
          <h2 style={{ color: COLORS.textPrimary, margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
            📊 Tool Summary Cards
          </h2>
          <span style={{ color: COLORS.textMuted, fontSize: '1.2rem' }}>{expandedSections.toolSummaries ? '▼' : '▶'}</span>
        </div>
        
        {expandedSections.toolSummaries && (
          <div style={{ padding: '25px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
              
              {/* Port Discovery (Naabu equivalent) */}
              {portSummary && (
                <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px', borderTop: `3px solid ${COLORS.neonBlue}` }}>
                  <h4 style={{ color: COLORS.neonBlue, marginBottom: '15px', fontSize: '1rem' }}>🔌 Port Discovery</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{portSummary.totalOpen || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Total Open</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: (portSummary.highRiskCount || 0) > 0 ? COLORS.severityCritical : COLORS.neonGreen, fontWeight: '700', fontSize: '1.3rem' }}>{portSummary.highRiskCount || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>High Risk</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: (portSummary.remoteAccessCount || 0) > 0 ? COLORS.severityMedium : COLORS.neonGreen, fontWeight: '700', fontSize: '1.3rem' }}>{portSummary.remoteAccessCount || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Remote Access</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: (portSummary.databaseCount || 0) > 0 ? COLORS.severityCritical : COLORS.neonGreen, fontWeight: '700', fontSize: '1.3rem' }}>{portSummary.databaseCount || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Databases</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Service Grabber (zgrab2 equivalent) */}
              {serviceSummary && (
                <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px', borderTop: `3px solid ${COLORS.neonBlue}` }}>
                  <h4 style={{ color: COLORS.neonBlue, marginBottom: '15px', fontSize: '1rem' }}>📡 Service Grabber</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{serviceSummary.servicesIdentified || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Services ID'd</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{serviceSummary.bannersCollected || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Banners</div>
                    </div>
                  </div>
                  {sshInfo.software && (
                    <div style={{ marginTop: '12px', background: '#0a0a0a', padding: '8px', borderRadius: '4px' }}>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>SSH: <span style={{ color: COLORS.neonGreen }}>{sshInfo.software}</span></div>
                    </div>
                  )}
                </div>
              )}
              
              {/* HTTP Probe (httpx equivalent) */}
              {httpSummary && (
                <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px', borderTop: `3px solid ${COLORS.neonBlue}` }}>
                  <h4 style={{ color: COLORS.neonBlue, marginBottom: '15px', fontSize: '1rem' }}>🌐 HTTP Probe</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{httpSummary.totalProbes || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Probes</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonGreen, fontWeight: '700', fontSize: '1.3rem' }}>{httpSummary.liveHosts || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Live Hosts</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{httpSummary.uniqueTechnologies || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Technologies</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: httpSummary.httpsEnabled ? COLORS.neonGreen : COLORS.severityMedium, fontWeight: '700', fontSize: '1.3rem' }}>
                        {httpSummary.httpsEnabled ? '✓' : '✗'}
                      </div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>HTTPS</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Web Crawler (Katana equivalent) */}
              {crawlingSummary && (
                <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px', borderTop: `3px solid ${COLORS.neonBlue}` }}>
                  <h4 style={{ color: COLORS.neonBlue, marginBottom: '15px', fontSize: '1rem' }}>🕷️ Web Crawler</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{crawlingSummary.totalUrls || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Total URLs</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{crawlingSummary.uniquePaths || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Unique Paths</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{crawlingSummary.parametersFound || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Params</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Subdomain Finder */}
              {subfinderSummary && (
                <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px', borderTop: `3px solid ${COLORS.neonBlue}` }}>
                  <h4 style={{ color: COLORS.neonBlue, marginBottom: '15px', fontSize: '1rem' }}>🔍 Subdomain Finder</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{subfinderSummary.total || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Total</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.severityMedium, fontWeight: '700', fontSize: '1.3rem' }}>{subfinderSummary.adminCount || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Admin</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.neonBlue, fontWeight: '700', fontSize: '1.3rem' }}>{subfinderSummary.interestingCount || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Interesting</div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Vulnerability Scanner (Nuclei equivalent) */}
              {vulnSummary && (
                <div style={{ background: COLORS.bgSecondary, borderRadius: '10px', padding: '20px', borderTop: `3px solid ${COLORS.brandRed}` }}>
                  <h4 style={{ color: COLORS.brandRed, marginBottom: '15px', fontSize: '1rem' }}>💥 Vulnerability Scanner</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: COLORS.brandRed, fontWeight: '700', fontSize: '1.3rem' }}>{vulnList.length || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Total (Nuclei + Nikto)</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: (vulnSummary.criticalCount || 0) > 0 ? COLORS.severityCritical : COLORS.neonGreen, fontWeight: '700', fontSize: '1.3rem' }}>{vulnSummary.criticalCount || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>Critical</div>
                    </div>
                    <div style={{ background: COLORS.bgCard, padding: '12px', borderRadius: '6px', textAlign: 'center' }}>
                      <div style={{ color: (vulnSummary.highCount || 0) > 0 ? COLORS.severityHigh : COLORS.neonGreen, fontWeight: '700', fontSize: '1.3rem' }}>{vulnSummary.highCount || 0}</div>
                      <div style={{ color: COLORS.textMuted, fontSize: '0.7rem' }}>High</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* ==================== RAW TOOL DATA ==================== */}
      <div style={{ 
        background: COLORS.bgCard, 
        border: `1px solid ${COLORS.borderDark}`,
        borderRadius: '10px',
        marginBottom: '25px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleSection('rawToolData')}
          style={{ 
            padding: '18px 25px', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: 'rgba(255,255,255,0.03)'
          }}
        >
          <h2 style={{ color: COLORS.textMuted, margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
            🛠️ Raw Tool Data (JSON)
          </h2>
          <span style={{ color: COLORS.textMuted, fontSize: '1.2rem' }}>{expandedSections.rawToolData ? '▼' : '▶'}</span>
        </div>
        
        {expandedSections.rawToolData && (
          <div style={{ padding: '25px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
              {[
                { name: 'dns', data: dns },
                { name: 'network.ports', data: ports },
                { name: 'network.services', data: networkServices },
                { name: 'web.httpProbes', data: httpProbes },
                { name: 'web.crawling', data: crawling },
                { name: 'web.waf', data: waf },
                { name: 'vulnerabilities', data: vulnerabilities },
                { name: 'ssl', data: ssl },
                { name: 'whois', data: whois },
                { name: 'executiveSummary', data: execSummary },
                { name: 'aiAnalysis', data: ai }
              ].map((tool, i) => (
                <div key={i} style={{ background: COLORS.bgSecondary, borderRadius: '8px', overflow: 'hidden' }}>
                  <div style={{ 
                    padding: '12px 15px', 
                    background: `${COLORS.neonBlue}15`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ color: COLORS.neonBlue, fontWeight: '700', textTransform: 'uppercase', fontSize: '0.9rem' }}>{tool.name}</span>
                    <button 
                      onClick={() => copyToClipboard(JSON.stringify(tool.data, null, 2))}
                      style={{ background: COLORS.neonGreen, border: 'none', borderRadius: '4px', padding: '4px 10px', color: '#000', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
                    >
                      Copy
                    </button>
                  </div>
                  <pre style={{ margin: 0, padding: '12px', background: '#0a0a0a', color: COLORS.neonGreen, fontSize: '0.7rem', maxHeight: '150px', overflow: 'auto', fontFamily: 'monospace' }}>
                    {JSON.stringify(tool.data, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* ==================== FULL JSON ==================== */}
      <div style={{ 
        background: COLORS.bgCard, 
        border: `1px solid ${COLORS.borderDark}`,
        borderRadius: '10px',
        marginBottom: '30px',
        overflow: 'hidden'
      }}>
        <div 
          onClick={() => toggleSection('fullJson')}
          style={{ 
            padding: '18px 25px', 
            cursor: 'pointer', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: 'rgba(255,255,255,0.03)'
          }}
        >
          <h2 style={{ color: COLORS.textMuted, margin: 0, fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
            📄 Full JSON Response
          </h2>
          <span style={{ color: COLORS.textMuted, fontSize: '1.2rem' }}>{expandedSections.fullJson ? '▼' : '▶'}</span>
        </div>
        
        {expandedSections.fullJson && (
          <div style={{ padding: '25px' }}>
            <button 
              onClick={() => copyToClipboard(JSON.stringify(results, null, 2))}
              style={{ marginBottom: '15px', padding: '12px 25px', background: COLORS.neonGreen, border: 'none', borderRadius: '6px', color: '#000', fontWeight: '700', cursor: 'pointer' }}
            >
              📋 Copy Full JSON
            </button>
            <pre style={{ 
              background: '#0a0a0a', 
              color: COLORS.neonGreen, 
              padding: '20px', 
              borderRadius: '8px', 
              overflow: 'auto', 
              fontFamily: 'monospace', 
              fontSize: '0.7rem',
              lineHeight: '1.5',
              maxHeight: '500px'
            }}>
              {JSON.stringify(results, null, 2)}
            </pre>
          </div>
        )}
      </div>
      
      {/* ==================== SCAN METADATA ==================== */}
      <div style={{ 
        background: COLORS.bgSecondary, 
        borderRadius: '10px', 
        padding: '20px',
        marginBottom: '30px'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>Scan ID: </span>
            <span style={{ color: COLORS.neonBlue, fontFamily: 'monospace', fontSize: '0.85rem' }}>{results.scanId || scanData.scanId}</span>
          </div>
          <div>
            <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>Version: </span>
            <span style={{ color: COLORS.textPrimary }}>{metadata.scannerVersion || scanData.scanVersion}</span>
          </div>
          <div>
            <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>Timestamp: </span>
            <span style={{ color: COLORS.textPrimary }}>{results.timestamp ? new Date(results.timestamp).toLocaleString() : 'N/A'}</span>
          </div>
          <div>
            <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>AI Model: </span>
            <span style={{ color: COLORS.textPrimary }}>{results.metadata?.analysisEngine || 'Claude'}</span>
          </div>
        </div>
        
        {metadata.toolsUsed?.length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <span style={{ color: COLORS.textMuted, fontSize: '0.75rem' }}>Tools Used: </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
              {metadata.toolsUsed.map((tool, i) => (
                <span key={i} style={{ background: COLORS.bgCard, padding: '4px 10px', borderRadius: '10px', fontSize: '0.8rem', color: COLORS.neonBlue }}>
                  {tool}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* ==================== FOOTER ==================== */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap', marginTop: '40px', paddingTop: '30px', borderTop: `1px solid ${COLORS.borderDark}` }}>
        <button onClick={() => navigate('/reports')} style={{ padding: '15px 40px', border: `2px solid ${COLORS.neonBlue}`, borderRadius: '6px', cursor: 'pointer', background: 'transparent', color: COLORS.neonBlue, fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
          View All Reports
        </button>
        <button
          onClick={() => navigate('/validate', {
            state: {
              mode: 'integrated',
              sourceType: 'scrutinize',
              sourceScanId: scanId,
              target: results?.target || results?.scanInfo?.target
              // Note: inheritedData is NOT passed - API will fetch from source scan blob
            }
          })}
          style={{ padding: '15px 40px', border: `2px solid ${COLORS.neonGreen}`, borderRadius: '6px', cursor: 'pointer', background: 'rgba(57, 255, 20, 0.15)', color: COLORS.neonGreen, fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '1.5px' }}
        >
          Validate Findings
        </button>
        <button onClick={() => navigate('/scrutinize')} style={{ padding: '15px 40px', border: `2px solid ${COLORS.brandRed}`, borderRadius: '6px', cursor: 'pointer', background: COLORS.brandRed, color: '#fff', fontWeight: '700', fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '1.5px', boxShadow: `0 0 25px ${COLORS.brandRed}66` }}>
          New Deep Scan
        </button>
      </div>
    </div>
  );
};

export default ScrutinizeResults;